import { api } from '../api/client'

/**
 * Notifiche push sul telefono (flag `notifiche.push`, `PushSubscriptionsController` sul server). Android/Chrome: si
 * attivano dal browser o dall'app installata. iPhone: solo con il CRM aggiunto alla schermata Home (iOS 16.4+), e il
 * permesso va chiesto da un tocco dell'utente, mai all'apertura della pagina.
 */

export interface PushDevice {
  id: number
  device: string
  createdAt: string
  lastSuccessAt: string | null
  active: boolean
  endpoint: string
}

export interface PushState {
  enabled: boolean
  publicKey: string | null
  devices: PushDevice[]
}

export const pushApi = {
  get: () => api<PushState>('/api/push'),
  subscribe: (sub: PushSubscriptionJSON, device: string) =>
    api<void>('/api/push/subscriptions', { method: 'POST', body: JSON.stringify({ ...sub, device }) }),
  removeById: (id: number) => api<void>(`/api/push/subscriptions?id=${id}`, { method: 'DELETE' }),
  removeByEndpoint: (endpoint: string) =>
    api<void>(`/api/push/subscriptions?endpoint=${encodeURIComponent(endpoint)}`, { method: 'DELETE' }),
  test: () => api<void>('/api/push/test', { method: 'POST' }),
}

export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

/** Cosa puo' fare questo dispositivo. */
export type PushSupport =
  | 'supported'
  /** iPhone/iPad in Safari: prima va aggiunto alla schermata Home. */
  | 'ios-needs-install'
  /** Browser senza notifiche push (o pagina non sicura). */
  | 'unsupported'

export function pushSupport(): PushSupport {
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext
  if (hasApis) return 'supported'
  if (isIos() && !isStandalone()) return 'ios-needs-install'
  return 'unsupported'
}

export function permission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

/** Nome leggibile del dispositivo, per l'elenco nelle preferenze. */
export function deviceName(): string {
  const ua = navigator.userAgent
  const os = /iphone/i.test(ua) ? 'iPhone' : /ipad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPad'
    : /android/i.test(ua) ? 'Android' : /windows/i.test(ua) ? 'Windows' : /mac os/i.test(ua) ? 'Mac' : /linux/i.test(ua) ? 'Linux' : 'Dispositivo'
  const browser = /edg\//i.test(ua) ? 'Edge' : /samsungbrowser/i.test(ua) ? 'Samsung Internet' : /firefox|fxios/i.test(ua) ? 'Firefox'
    : /crios|chrome/i.test(ua) ? 'Chrome' : /safari/i.test(ua) ? 'Safari' : ''
  const app = isStandalone() ? 'app' : browser
  return app ? `${os} · ${app}` : os
}

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/')
  if (existing) return existing
  // In sviluppo il service worker non si registra da solo (main.tsx): lo si registra qui quando serve.
  await navigator.serviceWorker.register('/sw.js')
  return navigator.serviceWorker.ready
}

/** L'iscrizione di questo browser, se c'e'. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'supported') return null
  const reg = await navigator.serviceWorker.getRegistration('/')
  return reg ? reg.pushManager.getSubscription() : null
}

/**
 * Chiede il permesso (va chiamata dal tocco di un pulsante), iscrive il dispositivo e lo registra sul server.
 * Ritorna `'denied'` se l'utente ha negato il permesso.
 */
export async function enablePush(publicKey: string): Promise<'ok' | 'denied'> {
  const result = await Notification.requestPermission()
  if (result !== 'granted') return 'denied'
  const reg = await registration()
  let sub = await reg.pushManager.getSubscription()
  const key = keyBytes(publicKey)
  // Iscrizione fatta con un'altra chiave (chiave del server rigenerata): va rifatta.
  if (sub && sub.options.applicationServerKey) {
    const current = new Uint8Array(sub.options.applicationServerKey)
    if (current.length !== key.length || current.some((b, i) => b !== key[i])) {
      await sub.unsubscribe()
      sub = null
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  await pushApi.subscribe(sub.toJSON(), deviceName())
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT))
  return 'ok'
}

/** Spegne le notifiche su questo dispositivo (browser e server). */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription()
  if (sub) {
    await pushApi.removeByEndpoint(sub.endpoint).catch(() => undefined)
    await sub.unsubscribe().catch(() => false)
  }
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT))
}

/** Riscrive sul server l'iscrizione gia' presente (es. dopo un cambio di utente sullo stesso telefono). */
export async function refreshSubscription(state: PushState): Promise<void> {
  if (!state.enabled || permission() !== 'granted') return
  const sub = await currentSubscription()
  if (!sub) return
  if (!state.devices.some(d => d.endpoint === sub.endpoint)) await pushApi.subscribe(sub.toJSON(), deviceName())
}

/** Lanciato dopo che le notifiche di questo dispositivo sono state accese o spente. */
export const PUSH_CHANGED_EVENT = 'ecrm:push-changed'
